import React, { useState, useEffect, useCallback } from 'react';
import { storage, awardPoints } from './storage';
import { useTenant } from './TenantContext';

// ─── CouplesTrivia ────────────────────────────────────────────────────────────
// Displays trivia questions about the couple. Supports two question types:
//   - multiple_choice: 4 options, one correct answer
//   - short_answer:    free-text, matched case-insensitively against acceptedAnswers
//
// Data lives at weddings/{weddingId}/shared/couples-trivia (via storage.js).
// Admin can manage the question bank via the in-app form.
// ─────────────────────────────────────────────────────────────────────────────

export default function CouplesTrivia({ guestName, isAdmin, onBack }) {
  const tenant = useTenant();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [answers, setAnswers] = useState({});       // questionId → { value, correct, pts }
  const [shortInput, setShortInput] = useState('');
  const [feedback, setFeedback] = useState(null);   // null | 'correct' | 'wrong'
  const [showAdmin, setShowAdmin] = useState(false);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const d = await storage.get('couples-trivia', true);
      if (d) {
        const all = JSON.parse(d.value);
        // Only show active questions; admin sees all
        setQuestions(isAdmin ? all : all.filter(q => q.active !== false));
      }
    } catch (e) {
      console.error('CouplesTrivia load error:', e);
    }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  const saveQuestions = async (qs) => {
    await storage.set('couples-trivia', JSON.stringify(qs), true);
    setQuestions(qs);
  };

  const activeQuestion = questions[activeIdx] || null;
  const alreadyAnswered = activeQuestion ? !!answers[activeQuestion.id] : false;

  // ── Submit answer ──────────────────────────────────────────────────────────
  const submitAnswer = async (value) => {
    if (!activeQuestion || alreadyAnswered) return;

    let correct = false;
    if (activeQuestion.type === 'multiple_choice') {
      correct = value === activeQuestion.correctAnswer;
    } else {
      // short_answer: case-insensitive match against acceptedAnswers list
      const normalized = value.trim().toLowerCase();
      correct = (activeQuestion.acceptedAnswers || []).some(
        a => a.trim().toLowerCase() === normalized
      );
    }

    const pts = correct ? (activeQuestion.points || 10) : 0;
    const result = { value, correct, pts };
    setAnswers(prev => ({ ...prev, [activeQuestion.id]: result }));
    setFeedback(correct ? 'correct' : 'wrong');
    setShortInput('');

    if (correct && pts > 0) {
      try { await awardPoints(guestName, pts); } catch (e) { console.error(e); }
    }
  };

  const goNext = () => {
    setFeedback(null);
    setShortInput('');
    setActiveIdx(i => Math.min(i + 1, questions.length - 1));
  };

  const goPrev = () => {
    setFeedback(null);
    setShortInput('');
    setActiveIdx(i => Math.max(i - 1, 0));
  };

  // ── Score summary ──────────────────────────────────────────────────────────
  const answeredCount = Object.keys(answers).length;
  const totalPts = Object.values(answers).reduce((sum, a) => sum + a.pts, 0);
  const correctCount = Object.values(answers).filter(a => a.correct).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  if (showAdmin && isAdmin) {
    return (
      <TriviaAdminPanel
        questions={questions}
        onSave={saveQuestions}
        onClose={() => { setShowAdmin(false); loadQuestions(); }}
        coupleNames={tenant.coupleNames}
      />
    );
  }

  return (
    <div className="min-h-full pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
        <button onClick={onBack} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <div className="flex-1 text-center">
          <h2 className="text-white font-bold text-lg">Couples Trivia</h2>
          <p className="text-primary/60 text-xs">{tenant.coupleNames}</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdmin(true)} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined">edit_note</span>
          </button>
        )}
        {!isAdmin && <div className="w-10" />}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <span className="material-symbols-outlined text-primary animate-spin" style={{fontSize:'36px'}}>autorenew</span>
        </div>
      ) : questions.length === 0 ? (
        <div className="text-center py-20 px-6">
          <span className="material-symbols-outlined text-primary/30" style={{fontSize:'56px'}}>quiz</span>
          <p className="text-slate-400 mt-4 text-sm">No trivia questions yet.</p>
          {isAdmin && (
            <button onClick={() => setShowAdmin(true)} className="mt-4 bg-primary text-white px-6 py-2 rounded-xl font-bold text-sm">
              Add Questions
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Score strip */}
          <div className="mx-6 mt-5 mb-4 flex items-center justify-between bg-primary/10 border border-primary/20 rounded-xl px-4 py-3">
            <div className="text-center">
              <p className="text-2xl font-black text-primary">{totalPts}</p>
              <p className="text-[10px] uppercase font-bold text-slate-500">Points Earned</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-white">{correctCount}/{answeredCount}</p>
              <p className="text-[10px] uppercase font-bold text-slate-500">Correct</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-slate-400">{questions.length - answeredCount}</p>
              <p className="text-[10px] uppercase font-bold text-slate-500">Remaining</p>
            </div>
          </div>

          {/* Question card */}
          {activeQuestion && (
            <div className="mx-6">
              {/* Progress */}
              <div className="flex items-center gap-2 mb-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Question {activeIdx + 1} of {questions.length}
                </p>
                <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${((activeIdx + 1) / questions.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Question */}
              <div className="bg-white/5 border border-primary/10 rounded-2xl p-5 mb-4">
                <p className="text-white text-lg font-bold leading-snug">{activeQuestion.question}</p>
                {activeQuestion.hint && (
                  <p className="text-slate-500 text-xs mt-2 italic">Hint: {activeQuestion.hint}</p>
                )}
              </div>

              {/* Feedback banner */}
              {feedback && (
                <div className={`rounded-xl px-4 py-3 mb-4 flex items-center gap-3 ${
                  feedback === 'correct'
                    ? 'bg-green-500/20 border border-green-500/30'
                    : 'bg-red-500/20 border border-red-500/30'
                }`}>
                  <span className={`material-symbols-outlined fill-icon ${feedback === 'correct' ? 'text-green-400' : 'text-red-400'}`}>
                    {feedback === 'correct' ? 'check_circle' : 'cancel'}
                  </span>
                  <div>
                    <p className={`font-bold text-sm ${feedback === 'correct' ? 'text-green-300' : 'text-red-300'}`}>
                      {feedback === 'correct' ? `Correct! +${activeQuestion.points || 10} pts` : 'Not quite!'}
                    </p>
                    {feedback === 'wrong' && activeQuestion.type === 'multiple_choice' && (
                      <p className="text-slate-400 text-xs mt-0.5">
                        Answer: {activeQuestion.correctAnswer}
                      </p>
                    )}
                    {feedback === 'wrong' && activeQuestion.type === 'short_answer' && (
                      <p className="text-slate-400 text-xs mt-0.5">
                        Accepted: {(activeQuestion.acceptedAnswers || []).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Answer UI */}
              {!alreadyAnswered && activeQuestion.type === 'multiple_choice' && (
                <div className="space-y-2">
                  {(activeQuestion.options || []).map((opt, i) => (
                    <button key={i} onClick={() => submitAnswer(opt)}
                      className="w-full text-left px-4 py-3 bg-white/5 border border-primary/15 rounded-xl text-white text-sm font-medium hover:bg-primary/20 hover:border-primary/40 transition">
                      <span className="text-primary font-bold mr-2">{String.fromCharCode(65 + i)}.</span>
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {!alreadyAnswered && activeQuestion.type === 'short_answer' && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shortInput}
                    onChange={e => setShortInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitAnswer(shortInput); }}
                    placeholder="Type your answer..."
                    className="flex-1 px-4 py-3 bg-white/5 border border-primary/20 rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:border-primary"
                  />
                  <button onClick={() => submitAnswer(shortInput)}
                    className="px-5 bg-primary text-white rounded-xl font-bold text-sm hover:opacity-90 transition">
                    Submit
                  </button>
                </div>
              )}

              {/* Already answered — show result */}
              {alreadyAnswered && !feedback && (
                <div className={`rounded-xl px-4 py-3 mb-4 flex items-center gap-3 ${
                  answers[activeQuestion.id].correct
                    ? 'bg-green-500/10 border border-green-500/20'
                    : 'bg-red-500/10 border border-red-500/20'
                }`}>
                  <span className={`material-symbols-outlined fill-icon text-sm ${answers[activeQuestion.id].correct ? 'text-green-400' : 'text-red-400'}`}>
                    {answers[activeQuestion.id].correct ? 'check_circle' : 'cancel'}
                  </span>
                  <p className="text-slate-400 text-sm">
                    You answered: <span className="text-white font-bold">{answers[activeQuestion.id].value}</span>
                  </p>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3 mt-5">
                {activeIdx > 0 && (
                  <button onClick={goPrev}
                    className="flex-1 py-3 bg-white/5 border border-primary/15 rounded-xl text-slate-400 font-bold text-sm hover:bg-white/10 transition">
                    ← Previous
                  </button>
                )}
                {activeIdx < questions.length - 1 && (
                  <button onClick={goNext}
                    className="flex-1 py-3 bg-primary/20 border border-primary/30 rounded-xl text-primary font-bold text-sm hover:bg-primary/30 transition">
                    Next →
                  </button>
                )}
                {activeIdx === questions.length - 1 && answeredCount === questions.length && (
                  <div className="flex-1 py-3 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400 font-bold text-sm text-center">
                    All done! 🎉 {totalPts} pts earned
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Admin Panel ──────────────────────────────────────────────────────────────
function TriviaAdminPanel({ questions, onSave, onClose, coupleNames }) {
  const [list, setList] = useState(questions);
  const [showForm, setShowForm] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);

  const deleteQ = (idx) => {
    if (window.confirm('Delete this question?')) {
      const updated = list.filter((_, i) => i !== idx);
      setList(updated);
      onSave(updated);
    }
  };

  const toggleActive = (idx) => {
    const updated = list.map((q, i) => i === idx ? { ...q, active: q.active === false } : q);
    setList(updated);
    onSave(updated);
  };

  const handleSaveQuestion = (q) => {
    let updated;
    if (editingIdx !== null) {
      updated = list.map((item, i) => i === editingIdx ? q : item);
    } else {
      updated = [...list, { ...q, id: Date.now().toString(), active: true }];
    }
    setList(updated);
    onSave(updated);
    setShowForm(false);
    setEditingIdx(null);
  };

  return (
    <div className="min-h-full pb-24">
      <div className="sticky top-0 z-10 flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
        <button onClick={onClose} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h2 className="text-white font-bold text-lg flex-1 text-center">Manage Trivia</h2>
        <button onClick={() => { setShowForm(true); setEditingIdx(null); }}
          className="flex size-10 items-center justify-center rounded-full bg-primary/20 text-primary">
          <span className="material-symbols-outlined">add</span>
        </button>
      </div>

      <div className="px-6 pt-5 space-y-3">
        {showForm && (
          <TriviaQuestionForm
            initial={editingIdx !== null ? list[editingIdx] : null}
            onSave={handleSaveQuestion}
            onCancel={() => { setShowForm(false); setEditingIdx(null); }}
          />
        )}

        {list.length === 0 && !showForm && (
          <div className="text-center py-16">
            <span className="material-symbols-outlined text-primary/30" style={{fontSize:'48px'}}>quiz</span>
            <p className="text-slate-400 mt-3 text-sm">No questions yet. Add some to get started!</p>
          </div>
        )}

        {list.map((q, idx) => (
          <div key={q.id || idx}
            className={`bg-white/5 border rounded-xl p-4 ${q.active === false ? 'opacity-50 border-white/10' : 'border-primary/15'}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    q.type === 'multiple_choice'
                      ? 'bg-primary/20 text-primary'
                      : 'bg-rose-500/20 text-rose-400'
                  }`}>
                    {q.type === 'multiple_choice' ? 'MC' : 'Short'}
                  </span>
                  <span className="text-[10px] text-slate-500 font-bold">+{q.points || 10} pts</span>
                  {q.active === false && <span className="text-[10px] text-amber-400 font-bold">HIDDEN</span>}
                </div>
                <p className="text-white text-sm font-medium">{q.question}</p>
                {q.type === 'multiple_choice' && (
                  <p className="text-slate-500 text-xs mt-1">Answer: {q.correctAnswer}</p>
                )}
                {q.type === 'short_answer' && (
                  <p className="text-slate-500 text-xs mt-1">Accepted: {(q.acceptedAnswers || []).join(', ')}</p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => toggleActive(idx)}
                  className="text-slate-400 hover:text-primary transition p-1">
                  <span className="material-symbols-outlined" style={{fontSize:'18px'}}>
                    {q.active === false ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
                <button onClick={() => { setEditingIdx(idx); setShowForm(true); }}
                  className="text-slate-400 hover:text-primary transition p-1">
                  <span className="material-symbols-outlined" style={{fontSize:'18px'}}>edit</span>
                </button>
                <button onClick={() => deleteQ(idx)}
                  className="text-slate-400 hover:text-red-400 transition p-1">
                  <span className="material-symbols-outlined" style={{fontSize:'18px'}}>delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Question Form ────────────────────────────────────────────────────────────
function TriviaQuestionForm({ initial, onSave, onCancel }) {
  const [type, setType] = useState(initial?.type || 'multiple_choice');
  const [question, setQuestion] = useState(initial?.question || '');
  const [options, setOptions] = useState(initial?.options || ['', '', '', '']);
  const [correctAnswer, setCorrectAnswer] = useState(initial?.correctAnswer || '');
  const [acceptedAnswers, setAcceptedAnswers] = useState((initial?.acceptedAnswers || []).join(', '));
  const [points, setPoints] = useState(initial?.points || 10);
  const [hint, setHint] = useState(initial?.hint || '');

  const inputCls = "w-full px-3 py-2 bg-white/5 border border-primary/20 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-primary";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    const base = { type, question: question.trim(), points: Number(points), hint: hint.trim() || undefined };
    if (type === 'multiple_choice') {
      const validOpts = options.filter(o => o.trim());
      if (validOpts.length < 2 || !correctAnswer.trim()) return;
      onSave({ ...base, options: validOpts, correctAnswer: correctAnswer.trim() });
    } else {
      const accepted = acceptedAnswers.split(',').map(s => s.trim()).filter(Boolean);
      if (accepted.length === 0) return;
      onSave({ ...base, acceptedAnswers: accepted });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-3">
      <h3 className="font-bold text-slate-100 text-sm">{initial ? 'Edit Question' : 'New Question'}</h3>

      {/* Type toggle */}
      <div className="flex gap-2">
        {['multiple_choice', 'short_answer'].map(t => (
          <button key={t} type="button" onClick={() => setType(t)}
            className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${
              type === t ? 'bg-primary text-white border-primary' : 'bg-white/5 text-slate-400 border-white/10'
            }`}>
            {t === 'multiple_choice' ? 'Multiple Choice' : 'Short Answer'}
          </button>
        ))}
      </div>

      <div>
        <label className="text-xs font-bold text-primary/60 uppercase tracking-widest block mb-1">Question</label>
        <textarea value={question} onChange={e => setQuestion(e.target.value)} rows={2}
          className={inputCls} placeholder="e.g. Where did the couple first meet?" />
      </div>

      {type === 'multiple_choice' && (
        <>
          <div>
            <label className="text-xs font-bold text-primary/60 uppercase tracking-widest block mb-1">Options</label>
            {options.map((opt, i) => (
              <input key={i} value={opt} onChange={e => setOptions(options.map((o, j) => j === i ? e.target.value : o))}
                className={`${inputCls} mb-2`} placeholder={`Option ${String.fromCharCode(65 + i)}`} />
            ))}
          </div>
          <div>
            <label className="text-xs font-bold text-primary/60 uppercase tracking-widest block mb-1">Correct Answer</label>
            <select value={correctAnswer} onChange={e => setCorrectAnswer(e.target.value)}
              className={`${inputCls} bg-background-dark`}>
              <option value="">Select correct option...</option>
              {options.filter(o => o.trim()).map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {type === 'short_answer' && (
        <div>
          <label className="text-xs font-bold text-primary/60 uppercase tracking-widest block mb-1">
            Accepted Answers <span className="normal-case text-slate-500 font-normal">(comma separated)</span>
          </label>
          <input value={acceptedAnswers} onChange={e => setAcceptedAnswers(e.target.value)}
            className={inputCls} placeholder="e.g. Bali, bali, BALI" />
        </div>
      )}

      <div className="flex gap-2">
        <div className="flex-1">
          <label className="text-xs font-bold text-primary/60 uppercase tracking-widest block mb-1">Points</label>
          <input type="number" value={points} onChange={e => setPoints(e.target.value)} min={1} max={100}
            className={inputCls} />
        </div>
        <div className="flex-1">
          <label className="text-xs font-bold text-primary/60 uppercase tracking-widest block mb-1">Hint (optional)</label>
          <input value={hint} onChange={e => setHint(e.target.value)} className={inputCls} placeholder="Optional hint" />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button type="submit" className="flex-1 bg-primary text-white py-2 rounded-xl font-bold text-sm">Save</button>
        <button type="button" onClick={onCancel} className="px-4 bg-white/5 text-slate-400 py-2 rounded-xl text-sm">Cancel</button>
      </div>
    </form>
  );
}
